import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api } from "../api/client";
import type { ApiKey } from "../api/types";
import { failureMessage } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import {
  ActionError, Badge, Button, Card, Confirm, Field, Modal, PageHeader, inputClass,
} from "../components/ui";
import { useSession } from "../store/session";
import { formatDateTime } from "./Calls";

// Llaves de API: el mecanismo con el que un sistema del cliente llama a la
// plataforma. No es el mismo que la sesión del portal y no son intercambiables
// (ADR-019).
//
// Lo que esta vista tiene que lograr es que nadie se quede con una llave
// olvidada y viva. Por eso la columna de último uso está antes que la de
// creación: una llave que nadie usó en semanas es candidata a revocar, y es
// información que no se tiene si hay que ir a buscarla.

export function ApiKeys() {
  const keys = usePagedResource<ApiKey>("/api/v1/api-keys");
  const permissions = useSession((s) => s.session?.permissions ?? []);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function revoke(key: ApiKey) {
    setActionError(null);
    setBusy(true);
    try {
      await api.delete(`/api/v1/api-keys/${key.id}`);
      setRevoking(null);
      await keys.reload(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo revocar la llave"));
    } finally {
      setBusy(false);
    }
  }

  const columnas = useMemo<ColumnDef<ApiKey, any>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Nombre",
        meta: { label: "Nombre" },
        cell: ({ row }) => (
          <>
            <div className="font-medium text-slate-100">{row.original.name}</div>
            <div className="text-xs text-slate-500">
              Creada el {formatDateTime(row.original.created_at)}
            </div>
          </>
        ),
      },
      {
        id: "prefix",
        accessorKey: "prefix",
        header: "Prefijo",
        meta: { label: "Prefijo", hideOnMobile: true, width: "9rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.prefix}…</span>
        ),
      },
      {
        id: "scopes",
        accessorFn: (key) => key.scopes.join(" "),
        header: "Permisos",
        enableSorting: false,
        meta: { label: "Permisos" },
        cell: ({ row }) => (
          <div className="flex max-w-md flex-wrap gap-1">
            {row.original.scopes.map((scope) => (
              <code
                key={scope}
                className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-slate-300"
              >
                {scope}
              </code>
            ))}
          </div>
        ),
      },
      {
        // Antes que la fecha de creación a propósito: una llave que nadie usó
        // en semanas es candidata a revocar.
        id: "last_used_at",
        accessorKey: "last_used_at",
        header: "Último uso",
        meta: { label: "Último uso" },
        cell: ({ row }) =>
          row.original.last_used_at === null ? (
            <span className="text-slate-600">nunca</span>
          ) : (
            <span className="text-slate-600">{formatDateTime(row.original.last_used_at)}</span>
          ),
      },
      {
        id: "estado",
        accessorFn: (key) => keyState(key).label,
        header: "Estado",
        meta: { label: "Estado", width: "9rem" },
        cell: ({ row }) => {
          const state = keyState(row.original);
          return (
            <>
              <Badge tone={state.tone}>{state.label}</Badge>
              {row.original.expires_at !== null && row.original.revoked_at === null && (
                <div className="mt-1 text-xs text-slate-500">
                  hasta {formatDateTime(row.original.expires_at)}
                </div>
              )}
            </>
          );
        },
      },
    ],
    [],
  );

  const acciones = useMemo<DataTableRowAction<ApiKey>[]>(
    () => [
      {
        label: "Revocar",
        tone: "danger",
        // Una llave ya revocada no se puede reactivar ni revocar otra vez.
        hidden: (key) => key.revoked_at !== null,
        onSelect: (key) => setRevoking(key),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Llaves de API"
        description="Autentican a los sistemas del cliente contra /api/v1. El secreto se muestra una sola vez: la plataforma guarda únicamente su hash y no puede recuperarlo."
        action={
          !creating && <Button onClick={() => { setCreating(true); setCreated(null); }}>Nueva llave</Button>
        }
      />

      {actionError !== null && <ActionError message={actionError} />}

      {created !== null && <SecretOnce key={created.id} apiKey={created} onDismiss={() => setCreated(null)} />}

      {creating && (
        <CreateKeyForm
          held={permissions}
          onCancel={() => setCreating(false)}
          onCreated={(key) => {
            setCreating(false);
            setCreated(key);
            void keys.reload(true);
          }}
        />
      )}

      {revoking !== null && (
        <Confirm
          title={`Revocar «${revoking.name}»`}
          description={
            <>
              La llave deja de funcionar de inmediato y no se puede reactivar. Todo sistema que
              la esté usando empezará a recibir <code>401</code>. Revisa antes la columna de
              último uso: {formatDateTime(revoking.last_used_at)}.
            </>
          }
          confirmLabel="Revocar la llave"
          busy={busy}
          onConfirm={() => void revoke(revoking)}
          onCancel={() => setRevoking(null)}
        />
      )}

      <DataTable<ApiKey>
        data={keys.data ?? []}
        columns={columnas}
        getRowId={(key) => key.id}
        loading={keys.loading}
        error={keys.error}
        onRetry={() => void keys.reload(false)}
        caption="Llaves de API"
        empty={{
          title: "Sin llaves de API",
          description:
            "Sin una llave activa, ningún sistema externo puede originar llamadas: el portal usa sesión, no llave.",
        }}
        search={{ placeholder: "Buscar por nombre o permiso…" }}
        filters={[
          {
            columnId: "estado",
            label: "Estado",
            options: [
              { value: "Activa", label: "Activas" },
              { value: "Expirada", label: "Expiradas" },
              { value: "Revocada", label: "Revocadas" },
            ],
          },
        ]}
        rowActions={acciones}
        hideableColumns
        urlStateKey="llaves"
        pagination={{
          pageSize: keys.enEstaPagina,
          hasNext: keys.hayMas,
          hasPrevious: keys.hayAnterior,
          onNext: keys.siguiente,
          onPrevious: keys.anterior,
        }}
      />
    </div>
  );
}

function keyState(key: ApiKey): { label: string; tone: "good" | "warn" | "bad" } {
  if (key.revoked_at !== null) return { label: "Revocada", tone: "bad" };
  if (key.expires_at !== null && new Date(key.expires_at) <= new Date()) {
    return { label: "Expirada", tone: "warn" };
  }
  return { label: "Activa", tone: "good" };
}

/**
 * SecretOnce muestra el secreto recién emitido.
 *
 * Se queda en pantalla hasta que la persona lo descarta a propósito: si se
 * cerrara solo, o al recargar la tabla, el secreto se perdería para siempre y
 * la única salida sería crear otra llave.
 */
function SecretOnce({ apiKey, onDismiss }: { apiKey: ApiKey; onDismiss: () => void }) {
  const [copied, setCopied] = useState<"idle" | "ok" | "failed">("idle");
  const secret = apiKey.secret;

  if (secret === undefined) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret as string);
      setCopied("ok");
    } catch {
      // El portapapeles falla sin contexto seguro o sin permiso del navegador.
      // Decirlo es mejor que un botón que aparenta haber copiado.
      setCopied("failed");
    }
  }

  return (
    <Card className="mb-6 border-emerald-500/30 bg-emerald-500/10 p-4">
      <p className="text-sm font-semibold text-emerald-200">
        Llave «{apiKey.name}» creada. Este secreto no se vuelve a mostrar.
      </p>
      <p className="mt-1 text-sm text-emerald-200">
        Guárdalo ahora en el gestor de secretos de tu sistema. La plataforma guarda solo su hash:
        si se pierde, hay que revocar esta llave y crear otra.
      </p>
      <code className="mt-3 block overflow-x-auto rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 font-mono text-xs text-slate-100">
        {secret}
      </code>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="secondary" onClick={() => void copy()}>
          {copied === "ok" ? "Copiado" : "Copiar"}
        </Button>
        <Button variant="secondary" onClick={onDismiss}>
          Ya lo guardé
        </Button>
        {copied === "failed" && (
          <span className="text-xs text-emerald-200">
            El navegador no permitió copiar: selecciónalo a mano.
          </span>
        )}
      </div>
    </Card>
  );
}

function CreateKeyForm({
  held,
  onCancel,
  onCreated,
}: {
  held: string[];
  onCancel: () => void;
  onCreated: (key: ApiKey) => void;
}) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresOn, setExpiresOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Solo se ofrecen los permisos que tiene quien está creando la llave. El
  // backend rechaza el resto con 403 (IntersectScopes): una llave nunca puede
  // más que su dueño, y ofrecer en la UI algo que el servidor va a rechazar
  // solo produce un error sin explicación.
  const available = SCOPES.filter((s) => held.includes(s.scope));
  const withheld = SCOPES.filter((s) => !held.includes(s.scope));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = { name, scopes };
      if (expiresOn !== "") {
        // El input da un día; la llave vale hasta el final de ese día en la
        // zona del navegador, que es lo que la persona quiso decir.
        body.expires_at = new Date(`${expiresOn}T23:59:59`).toISOString();
      }
      onCreated(await api.post<ApiKey>("/api/v1/api-keys", body));
    } catch (err) {
      setError(failureMessage(err, "no se pudo crear la llave"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Nueva clave de API"
      description="El secreto se muestra una sola vez, al crearla."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label="Nombre"
          hint="Para saber qué sistema la usa cuando haya que revocarla. Máximo 120 caracteres."
        >
          <input
            className={inputClass}
            required
            maxLength={120}
            placeholder="CRM producción"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <fieldset>
          <legend className="text-sm font-medium text-slate-300">Permisos</legend>
          <p className="mt-1 text-xs text-slate-500">
            Da solo los que el sistema necesita. Una llave de marcación no necesita administrar
            usuarios ni leer la bitácora.
          </p>
          <div className="mt-2 grid gap-1 sm:grid-cols-2">
            {available.map((s) => (
              <label key={s.scope} className="flex items-start gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={scopes.includes(s.scope)}
                  onChange={(e) =>
                    setScopes((current) =>
                      e.target.checked
                        ? [...current, s.scope]
                        : current.filter((x) => x !== s.scope),
                    )
                  }
                />
                <span>
                  <code className="text-xs">{s.scope}</code>
                  <span className="block text-xs text-slate-500">{s.label}</span>
                </span>
              </label>
            ))}
          </div>
          {withheld.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              No se listan {withheld.length} permisos que tu rol no tiene: una llave no puede más
              que quien la crea.
            </p>
          )}
        </fieldset>

        <Field
          label="Expira"
          hint="Opcional. Una llave con fecha de caducidad limita el daño de una fuga que nadie notó."
          error={error ?? undefined}
        >
          <input
            className={inputClass}
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
        </Field>

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || scopes.length === 0}>
            {submitting ? "Creando…" : "Crear llave"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
        {scopes.length === 0 && (
          <p className="text-xs text-slate-500">Hace falta al menos un permiso.</p>
        )}
      </form>
    </Modal>
  );
}

// El catálogo del contrato (components.schemas.Scope en api/openapi.yaml). Se
// escribe aquí con su descripción en español porque el enum de OpenAPI no
// lleva etiquetas legibles.
const SCOPES: { scope: string; label: string }[] = [
  { scope: "calls.create", label: "Originar llamadas" },
  { scope: "calls.read", label: "Consultar llamadas, intentos y eventos" },
  { scope: "calls.manage", label: "Modificar y colgar llamadas en curso" },
  { scope: "voice_apps.read", label: "Consultar aplicaciones de voz" },
  { scope: "voice_apps.manage", label: "Crear y editar aplicaciones de voz" },
  { scope: "numbers.read", label: "Consultar números" },
  { scope: "numbers.manage", label: "Configurar la ruta entrante de un número" },
  { scope: "caller_ids.manage", label: "Registrar y verificar caller IDs" },
  { scope: "api_keys.manage", label: "Crear y revocar llaves de API" },
  { scope: "users.manage", label: "Invitar y administrar usuarios" },
  { scope: "webhooks.read", label: "Consultar endpoints y entregas" },
  { scope: "webhooks.manage", label: "Registrar endpoints y reenviar entregas" },
  { scope: "billing.read", label: "Consultar saldo y consumo" },
  { scope: "audit.read", label: "Consultar la bitácora de auditoría" },
  { scope: "trunks.manage", label: "Administrar troncales (plataforma)" },
  { scope: "tenants.manage", label: "Administrar cuentas (plataforma)" },
];
